import clsx from 'clsx';
import { useTopic } from 'hooks/useTopic';
import { FC, Fragment, useEffect } from 'react';
import { Answer, Task } from 'types/elearning';
import { DropZone } from './drop-zone';
import { ImagePlaceholder } from './image-placeholder';
import cls from './match-image-with-description.module.scss';
import { useMatchImageWithDescription } from './useMatchImageWithDescription';
import { useSelector } from 'react-redux';
import { selectTaskAnswerById } from 'store/topic/topicSlice';

interface IProps {
    task: Task;
    isChecked: boolean;
    correctAnswer: Answer['correct_answer'];
    isQuiz: boolean;
}

export const MatchImageWithDescription: FC<IProps> = ({
    task,
    isChecked,
    correctAnswer,
    isQuiz,
}) => {
    const {
        matchTextWithDescriptionCurrentAnswer,
        handleSetMatchTextWithDescriptionCurrentAnswer,
        handleSetLearningAnswersSingle,
        learningAnswers,
    } = useTopic();

    const taskAnswer = useSelector(selectTaskAnswerById(task.id));
    const { isCorrect } = useMatchImageWithDescription(taskAnswer);
    
    const clsWrapper = clsx(cls.wrapper, {
        [cls.wrapperDisabled]: isChecked,
    });

    const isAllFilled = Object.values(
        matchTextWithDescriptionCurrentAnswer || {}
    ).every((value) => value && value.trim() !== '');

    useEffect(() => {
        if (!isAllFilled || !task?.id || !matchTextWithDescriptionCurrentAnswer)
            return;

        const entries = Object.entries(matchTextWithDescriptionCurrentAnswer);
        if (!entries.length) return;

        const alreadySaved = learningAnswers?.[task.id];
        if (alreadySaved) return;

        entries.forEach(([questionId, answerId]) => {
            if (answerId && answerId.trim() !== '') {
                handleSetLearningAnswersSingle(task.id, questionId, answerId);
            }
        });
    }, [isAllFilled, task?.id]);

    return (
        <div className={clsWrapper}>
            <div className={cls.dropArea}>
                {Object?.entries?.(task?.content?.descriptions)?.map?.(
                    ([key, description], id) => {
                        const image_url = task?.content?.images?.[
                            matchTextWithDescriptionCurrentAnswer?.[key]
                        ] as string;

                        return (
                            <Fragment
                                key={`${key}-${description}-${id}-${image_url}`}
                            >
                                <DropZone
                                    id={key}
                                    description={description as string}
                                    image_url={image_url}
                                    isCorrect={isCorrect(key)}
                                    image={
                                        Object?.entries?.(
                                            task?.content?.images
                                        )?.find?.(
                                            ([imgKey]) =>
                                                imgKey ===
                                                matchTextWithDescriptionCurrentAnswer?.[
                                                    key
                                                ]
                                        ) as [string, string]
                                    }
                                    handleDrop={
                                        handleSetMatchTextWithDescriptionCurrentAnswer
                                    }
                                />
                            </Fragment>
                        );
                    }
                )}
            </div>
            <div className={cls.imagesArea}>
                {Object?.entries?.(task?.content?.images)?.map?.(
                    ([key, image_url], id) => (
                        <Fragment key={`${key}-${image_url}-${id}`}>
                            <ImagePlaceholder
                                id={key}
                                image_url={image_url as string}
                                isEmpty={
                                    matchTextWithDescriptionCurrentAnswer &&
                                    Object?.values?.(
                                        matchTextWithDescriptionCurrentAnswer
                                    )?.includes?.(key)
                                }
                            />
                        </Fragment>
                    )
                )}
            </div>
        </div>
    );
};
